import {
  IAIWorkspace,
  useCreateDraftVetRequest,
  useGetAiWorkspaceRequest,
  useRunDraftVetCompliance,
  useUpdateVetRequestPrompt,
} from "@entities/aiWorkspace";
import { ToastService } from "@shared/lib/services";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  DRAFT_VET_STEPS,
  ModuleProgress,
  vetDetailStep,
} from "../ModuleProgress";
import { aiWorkspaceRequestId } from "../useAiWorkspaceModuleParams";
import { useLocalRequestPrompt } from "../useLocalRequestPrompt";
import { WorkspacePromptEditor } from "../WorkspacePromptEditor";
import { promptToText } from "../WorkspacePromptEditor/promptToText";
import { assetsFromDraft, draftWasChecked } from "./DraftAssetPreview";
import { DraftReviewPanel } from "./DraftReviewPanel";
import { DraftMediaReports } from "./DraftMediaReports";
import { filesToDraftAssets } from "./filesToDraftAssets";
import styles from "./styles.module.scss";

export function DraftVetDetail({
  requestId,
  workspace,
  onBack,
  onOpenRequest,
}: {
  requestId: string;
  workspace: IAIWorkspace;
  onBack: () => void;
  onOpenRequest: (requestId: string, type: string) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { data, isFetching, refetch } = useGetAiWorkspaceRequest(requestId);
  const request = data?.data?.type === "draft-vet" ? data.data : undefined;
  const { promptHtml, initialHtml, updatePrompt, contentKey } =
    useLocalRequestPrompt(
      workspace._id,
      "draft-vet",
      request?.masterPrompt || "",
      requestId
    );
  const [committedPrompt, setCommittedPrompt] = useState("");
  const [saveVersion, setSaveVersion] = useState(0);
  const { update: saveRequestPrompt, isUpdating } = useUpdateVetRequestPrompt({
    onSuccess: (prompt) => {
      setCommittedPrompt(prompt);
      setSaveVersion((version) => version + 1);
    },
  });
  const { run, isRunning } = useRunDraftVetCompliance();
  const { create, isCreating } = useCreateDraftVetRequest((created) => {
    const id = aiWorkspaceRequestId(created);
    if (id) onOpenRequest(id, "draft-vet");
  });
  const assets = assetsFromDraft(request?.drafting);
  const [selectedId, setSelectedId] = useState("");
  const selected =
    assets.find((asset) => asset._id === selectedId) ?? assets[0];
  const compliancePending = assets.some(
    (asset) => asset.complianceCheckStatus === "Pending"
  );
    const reportRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!compliancePending && !isRunning) return;
    const timer = window.setInterval(() => {
      void refetch();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [compliancePending, isRunning, refetch]);
  const busy = isUploading || isCreating;
  const savedRequestPrompt = committedPrompt || request?.masterPrompt || "";
  const currentPrompt = promptToText(promptHtml).trim();
  const promptChanged =
    currentPrompt.length > 0 &&
    currentPrompt !== promptToText(savedRequestPrompt).trim();
  const runCheck = (mediaAssetId: string) => {
    if (!request || !mediaAssetId) return;
    run({
      aiWorkspaceRequestId: aiWorkspaceRequestId(request),
      draftingVersionId: request.drafting?.latestDraftingVersionId || "",
      mediaAssetId,
    });
  };

  const onFiles = async (list: FileList | null) => {
    const files = list ? Array.from(list) : [];
    if (!files.length || !request) return;
    setIsUploading(true);
    try {
      const mediaAssets = await filesToDraftAssets(files);
      create({
        aiWorkspaceId: workspace._id,
        masterPrompt: promptHtml,
        mediaAssets,
      });
    } catch {
      ToastService.error("Failed to upload draft");
    } finally {
      setIsUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!request && isFetching) {
    return <p className={styles.statusLine}>Loading draft check…</p>;
  }
  if (!request) {
    return <p className={styles.statusLine}>Draft check not found.</p>;
  }

  const scrollToReport = () => {
    window.setTimeout(() => {
      reportRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handleRunCheck = (mediaAssetId: string) => {
    runCheck(mediaAssetId);
    scrollToReport();
  };

  return (
    <div className={styles.tab}>
      <button type="button" className={styles.backBtn} onClick={onBack}>
        <ArrowLeft size={14} /> Back to all drafts
      </button>
      <ModuleProgress
        steps={DRAFT_VET_STEPS}
        current={vetDetailStep(
          compliancePending || isRunning || draftWasChecked(assets, false)
        )}
      />
      <WorkspacePromptEditor
        moduleId="draft-vet"
        aiWorkspaceId={workspace._id}
        label="Request prompt"
        hint="Save it on this check. Re-run after it changes."
        savedPrompt={initialHtml}
        committedPrompt={savedRequestPrompt}
        contentKey={contentKey}
        isSaving={isUpdating}
        saveVersion={saveVersion}
        requestOnly
        onSave={(prompt) =>
          saveRequestPrompt({
            aiWorkspaceRequestId: requestId,
            prompt: prompt,
          })
        }
        onHtmlChange={updatePrompt}
      />
      <div className={styles.listHeader}>
        {assets.length < 2 ? (
          <h2 className={styles.detailTitle}>
            {selected?.name || "Draft check"}
          </h2>
        ) : (
          <span />
        )}
        <button
          type="button"
          className={styles.secondaryBtn}
          disabled={busy}
          onClick={() => fileRef.current?.click()}
        >
          {busy ? <Loader2 size={14} className="spin-animation" /> : null}
          Upload new assets
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,video/*"
          multiple
          className={styles.hiddenInput}
          onChange={(event) => void onFiles(event.target.files)}
        />
      </div>
      <DraftReviewPanel
        assets={assets}
        selectedId={selected?._id || ""}
        onSelect={(id) => {
          setSelectedId(id);
          scrollToReport();
        }}
        draftingId={request.drafting?._id || ""}
        draftingVersionId={request.drafting?.latestDraftingVersionId || ""}
        analysis={
          <>
            <button
              type="button"
              className={styles.primaryBtn}
              disabled={isRunning || !selected?._id}
              onClick={() => selected?._id && handleRunCheck(selected._id)}
            >
              {isRunning ? <Loader2 size={14} className="spin-animation" /> : null}
              {selected && draftWasChecked([selected], false)
                ? "Re-run draft check"
                : "Run draft check"}
            </button>
            <div ref={reportRef}>
              <DraftMediaReports
                assets={selected ? [selected] : []}
                requestPromptId={request.masterPromptId}
                promptChanged={promptChanged}
                isRunning={isRunning}
                onRerun={handleRunCheck}
              />
            </div>
          </>
        }
      />
    </div>
  );
}
